/***
 *
 *   CATEGORY INTEGRATION ACTIONS
 *   Create-from and import-categories dropdowns driven by connected
 *   integrations (ClickUp list create / clone / space import). Used on
 *   project settings after the tickets-page category rail was removed.
 *
 **********/

import React, { useRef, useContext } from 'react';
import { Button, useAttach, ViewContext } from 'components/lib';
import { useQuery } from 'react-query';
import { useApiContext } from 'app/api';
import Dropdown from 'components/dropdown/dropdown';
import integrationsConfig from 'config/integrations';
import { AuthContext } from 'app/auth';
import { useTranslate } from 'app/translations';

export function CategoryIntegrationActions({ projectId, onSuccess, className }) {
    const { _t } = useTranslate();
    const { callApi } = useApiContext();
    const createRef = useRef(null);
    const importRef = useRef(null);
    const viewContext = useContext(ViewContext);
    // Same gate as the category filter's manage link and the project settings
    // route these dropdowns write into: AuthContext's resolved permission map,
    // not a string match on `user.permission` (which missed `master`).
    const { permission } = useContext(AuthContext);
    const canManage = !!permission?.admin;

    const integrations = useQuery(
        'integrations',
        () => callApi({ url: '/api/integration', permission: 'admin' }),
        { enabled: canManage },
    );

    const integrationData = integrations.data?.data || [];

    const integrationsDataType = integrationData.filter(
        (integration, index, self) =>
            index === self.findIndex((t) => t.service_name === integration.service_name),
    );

    const createList = [];
    const importList = [];

    if (canManage) {
        integrationsDataType?.forEach((integration) => {
            const integrationConfig = integrationsConfig[integration.service_name];
            const categoryConfig = integrationConfig?.ticket?.category;
            if (!categoryConfig) return;

            if (categoryConfig.create) {
                const CreateConnector = categoryConfig.create.connector;
                createList.push({
                    label: categoryConfig.create.label,
                    action: () => {
                        viewContext.modal.show(
                            {
                                title: _t('categories.create'),
                                component: <CreateConnector projectId={projectId} />,
                            },
                            () => {
                                onSuccess?.();
                            },
                        );
                    },
                });

                const CloneConnector = categoryConfig.clone.connector;
                createList.push({
                    label: categoryConfig.clone.label,
                    action: () => {
                        viewContext.modal.show(
                            {
                                title: _t('categories.existing.createFrom'),
                                component: <CloneConnector projectId={projectId} />,
                            },
                            () => {
                                onSuccess?.();
                            },
                        );
                    },
                });
            }

            if (categoryConfig.import) {
                const ImportConnector = categoryConfig.import.connector;
                importList.push({
                    label: categoryConfig.import.label,
                    action: () => {
                        viewContext.modal.show(
                            {
                                title: _t('categories.import'),
                                component: <ImportConnector projectId={projectId} />,
                            },
                            () => {
                                onSuccess?.();
                            },
                        );
                    },
                });
            }
        });
    }

    const { Component: CreateDropdown, toggle: createToggle } = useAttach({
        ref: createRef,
        position: 'bottom',
        offset: 5,
        children: <Dropdown list={createList} />,
    });

    const { Component: ImportDropdown, toggle: importToggle } = useAttach({
        ref: importRef,
        position: 'bottom',
        offset: 5,
        children: <Dropdown list={importList} />,
    });

    if (!canManage) return null;

    return (
        <div className={className || 'flex flex-wrap gap-2'}>
            <div ref={createRef} className="relative">
                <Button
                    text={_t('createFrom')}
                    variant="secondary"
                    size="sm"
                    action={() => {
                        createToggle();
                    }}
                />
                <CreateDropdown />
            </div>
            <div ref={importRef} className="relative">
                <Button
                    text={_t('categories.import')}
                    variant="secondary"
                    size="sm"
                    action={() => {
                        importToggle();
                    }}
                />
                <ImportDropdown />
            </div>
        </div>
    );
}
